/**
 * <s-gift-recipient> owns the gift card recipient form on gift card product pages.
 * The fieldset stays disabled (so nothing is submitted) until the shopper ticks
 * "send as a gift"; then Recipient email is required and the timezone offset is
 * filled for scheduled sends. Values survive the product-form partial re-render
 * that happens when a different denomination is chosen.
 */
const memory = { checked: false, values: {} };

class SGiftRecipient extends HTMLElement {
  #ac = new AbortController();

  connectedCallback() {
    const signal = this.#ac.signal;
    const toggle = this.querySelector('[data-recipient-toggle]');
    const fieldset = this.querySelector('[data-recipient-fields]');
    const offset = this.querySelector('[data-recipient-offset]');
    const date = this.querySelector('[data-recipient-date]');
    if (!(toggle instanceof HTMLInputElement) || !(fieldset instanceof HTMLFieldSetElement)) return;

    if (offset instanceof HTMLInputElement) offset.value = String(new Date().getTimezoneOffset());
    if (date instanceof HTMLInputElement) {
      const today = new Date();
      const max = new Date();
      max.setDate(max.getDate() + 90);
      date.min = isoDate(today);
      date.max = isoDate(max);
    }

    fieldset.querySelectorAll('input, textarea').forEach((field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
      if (field.type === 'hidden') return;
      if (memory.values[field.name] !== undefined) field.value = memory.values[field.name];
    });
    toggle.checked = memory.checked;
    this.#apply(toggle, fieldset);

    toggle.addEventListener('change', () => {
      memory.checked = toggle.checked;
      this.#apply(toggle, fieldset);
      if (toggle.checked) fieldset.querySelector('input[type="email"]')?.focus();
    }, { signal });

    fieldset.addEventListener('input', (event) => {
      const t = event.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
        memory.values[t.name] = t.value;
        const counter = this.querySelector('[data-recipient-count]');
        if (counter && t instanceof HTMLTextAreaElement) counter.textContent = `${t.value.length}/${t.maxLength}`;
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac.abort();
  }

  #apply(toggle, fieldset) {
    fieldset.disabled = !toggle.checked;
    fieldset.hidden = !toggle.checked;
    const email = fieldset.querySelector('input[type="email"]');
    if (email instanceof HTMLInputElement) email.required = toggle.checked;
  }
}

function isoDate(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

if (!customElements.get('s-gift-recipient')) {
  customElements.define('s-gift-recipient', SGiftRecipient);
}
